/**
 * The TO/CC choice next to a person who is already on an invoice, and the question it asks
 * (SPEC-christa-contacts-identity R2 AC13).
 *
 * Before this the Bill To row only DISPLAYED the role. The Keith case (production A-197, 2026-10-05): the project had
 * Keith as CC, she asked for him as TO, and nothing next to him changed it. A person who is saved for the project is
 * asked about the project too; everyone else changes on this invoice only. Invoices already made keep the people they
 * have (the server touches this invoice's row and the project's saved role, nothing else).
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export type Role = 'to' | 'cc';
export const ROLE_OPTION_LABEL: Record<Role, string> = { to: 'Invoices go to (TO)', cc: 'Copied on (CC)' };
const ROLE_WORD: Record<Role, string> = { to: 'TO', cc: 'CC' };

/** Statuses where the recipients are a record of what was sent or billed: no role control (display-only badge). */
export const ROLE_LOCKED_STATUSES = ['sent', 'overdue', 'partially_paid', 'paid', 'cancelled'];

export function RecipientRoleSelect({ personName, role, onChoose }: {
  personName: string;
  role: string;
  onChoose: (next: Role) => void;
}) {
  return (
    <select
      aria-label={`Change role for ${personName}`}
      value={role === 'to' ? 'to' : 'cc'}
      onChange={(event) => onChoose(event.target.value as Role)}
      className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
      data-testid="recipient-role-select"
    >
      <option value="to">{ROLE_OPTION_LABEL.to}</option>
      <option value="cc">{ROLE_OPTION_LABEL.cc}</option>
    </select>
  );
}

export interface RoleChangeRequest {
  personName: string;
  newRole: Role;
  /** "A-197 Hotel Renovation" when the person is saved for the project, else null. */
  savedForProject: string | null;
}

export function RoleChangeDialog({ request, busy, onConfirm, onCancel }: {
  request: RoleChangeRequest | null;
  busy: boolean;
  onConfirm: (choice: 'invoice' | 'project') => void;
  onCancel: () => void;
}) {
  const [choice, setChoice] = useState<'invoice' | 'project'>('project');
  useEffect(() => { if (request) setChoice('project'); }, [request]);
  const word = request ? ROLE_WORD[request.newRole] : '';
  return (
    <Dialog open={!!request} onOpenChange={(open) => { if (!open && !busy) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="role-change-dialog">
        {request && <>
          <DialogHeader>
            <DialogTitle>{`Change ${request.personName} to ${word}`}</DialogTitle>
            <DialogDescription>
              {request.savedForProject
                ? `${request.personName} is saved for ${request.savedForProject}. Change them to ${word} on this invoice and new invoices for ${request.savedForProject}? Invoices already made keep the people they have.`
                : `${request.personName} changes to ${word} on this invoice only.`}
            </DialogDescription>
          </DialogHeader>
          {request.savedForProject && (
            <div role="radiogroup" aria-label="Where should the change apply?" className="space-y-2">
              {([['project', 'This invoice and future invoices'], ['invoice', 'This invoice only']] as const).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="role-change-scope" value={value} checked={choice === value}
                    onChange={() => setChoice(value)} />
                  {label}
                </label>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button type="button" onClick={() => onConfirm(request.savedForProject ? choice : 'invoice')} disabled={busy}>
              Change role
            </Button>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}
